import { useEffect, useState, useRef } from 'react';

// Utils
import { getKambiConfig } from '../utils/utils';

// Hooks
import { useScript } from './use-script';
import { useKambiClean } from './use-kambi-clean';
import { useKambiWidget } from './use-kambi-widget';
import { useKambiWebsockets } from './use-kambi-websockets';
import useUserContext from '../../../hooks/useUserContext';
import sleep from '../../../Xmerald-UI/utilities/sleep';

// URL del script de Kambi
const KAMBI_SCRIPT_URL = process.env.REACT_APP_KAMBI_SCRIPT_URL

if (!KAMBI_SCRIPT_URL) {
  console.error('KAMBI_SCRIPT_URL is not defined in the environment');
}

// Flag global para prevenir inicialización de Kambi si el elemento no existe
window.__KAMBI_ALLOW_INIT = false;

export const useKambiBootstrap = () => {
  const [isBootstrapReady, setIsBootstrapReady] = useState(false);
  const { isLogged } = useUserContext();
  const { isScriptError, loadScript } = useScript(KAMBI_SCRIPT_URL);
  const isMountedRef = useRef(true);
  const bootstrapPromiseRef = useRef(null);
  const isLoggedRef = useRef(isLogged);
  const elementObserverRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const startBootstrapRef = useRef(null);
  const inconsistentStateCheckerRef = useRef(null);
  const initialObserverRef = useRef(null);

  // Mantener el ref actualizado
  useEffect(() => {
    isLoggedRef.current = isLogged;
  }, [isLogged]);

  useKambiWebsockets();
  useKambiClean(isLogged && isBootstrapReady);
  useKambiWidget(isBootstrapReady);

  useEffect(() => {
    isMountedRef.current = true;

    // Función para limpiar estado de Kambi si está en proceso de carga
    const cleanupIncompleteBootstrap = () => {
      // console.log('Limpiando bootstrap incompleto de Kambi...');
      
      // Desactivar el flag de inicialización
      window.__KAMBI_ALLOW_INIT = false;
      
      // Limpiar intervalos y observadores
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      
      if (elementObserverRef.current) {
        elementObserverRef.current.disconnect();
        elementObserverRef.current = null;
      }
      
      if (initialObserverRef.current) {
        initialObserverRef.current.disconnect();
        initialObserverRef.current = null;
      }
      
      // Limpiar instancia de Kambi si existe
      if (window?._kbc && typeof window?._kbc?.dispose === 'function') {
        try {
          window._kbc.dispose().catch(() => {
            // Ignorar errores de dispose si ya está en proceso de limpieza
          });
        } catch (error) {
          console.error('Error al limpiar Kambi durante bootstrap:', error);
        }
      }
      
      // Limpiar referencias globales
      if (window._kbc) {
        delete window._kbc;
      }
      
      if (window._kc) {
        delete window._kc;
      }
      
      // Limpiar el contenedor si existe
      const kambiBCElement = document.getElementById('KambiBC');
      if (kambiBCElement) {
        kambiBCElement.innerHTML = '';
      }
      
      // Resetear el estado para permitir reinicio completo
      setIsBootstrapReady(false);
    };
    
    // Limpiar cualquier estado inconsistente al montar
    // Esto asegura que si hubo un problema anterior, se reinicie limpiamente
    const kambiBCElement = document.getElementById('KambiBC');
    const hasInconsistentState = (window._kbc || window._kc) && (!kambiBCElement || !isBootstrapReady);
    
    if (hasInconsistentState) {
      // console.log('🧹 Detectado estado inconsistente de Kambi al montar, limpiando...');
      cleanupIncompleteBootstrap();
    }
    
    // Verificar periódicamente si hay un estado inconsistente y limpiarlo
    // Esto ayuda a recuperarse de errores que pueden ocurrir después del montaje inicial
    // Solo verifica si hay referencias globales pero el elemento no existe (no si está en proceso de carga)
    inconsistentStateCheckerRef.current = setInterval(() => {
      if (!isMountedRef.current) {
        if (inconsistentStateCheckerRef.current) {
          clearInterval(inconsistentStateCheckerRef.current);
          inconsistentStateCheckerRef.current = null;
        }
        return;
      }
      
      const element = document.getElementById('KambiBC');
      // Solo limpiar si hay referencias globales pero el elemento NO existe
      // No limpiar si el elemento existe pero no está listo (puede estar cargando)
      const hasStateButNoElement = (window._kbc || window._kc) && !element;
      
      if (hasStateButNoElement) {
        // console.log('🧹 Detectado estado inconsistente persistente (referencias sin elemento), limpiando...');
        cleanupIncompleteBootstrap();
      }
    }, 5000);

    // Función para verificar si un error está relacionado con Kambi y limpiar si es necesario
    const handleKambiError = (error, errorMessage) => {
      const message = errorMessage || (error && error.message) || '';
      const isKambiRelated = 
        message.includes('Kambi') ||
        message.includes('kambi') ||
        message.includes('Cannot read properties') ||
        message.includes('Cannot read property') ||
        message.includes('reading') ||
        message.includes('null') ||
        message.includes('undefined') ||
        (error && error.stack && error.stack.includes('kambi'));

      if (isKambiRelated) {
        const kambiBCElement = document.getElementById('KambiBC');
        const shouldCleanup = !isMountedRef.current || !kambiBCElement;
        
        if (shouldCleanup) {
          // console.warn('Error de Kambi detectado (componente desmontado o elemento faltante):', message);
          cleanupIncompleteBootstrap();
          
          // Si el componente está montado pero el elemento no existe, intentar reiniciar después de un breve delay
          if (isMountedRef.current && !kambiBCElement && startBootstrapRef.current) {
            setTimeout(() => {
              const element = document.getElementById('KambiBC');
              if (element && isMountedRef.current && startBootstrapRef.current) {
                // console.log('🔄 Intentando reiniciar bootstrap de Kambi después del error...');
                startBootstrapRef.current();
              }
            }, 500);
          }
          
          return true; // Prevenir que el error se propague
        } else if (isMountedRef.current && kambiBCElement) {
          // Si hay un error pero el componente está montado y el elemento existe,
          // puede ser un estado inconsistente, forzar limpieza y reinicio
          // console.warn('Error de Kambi detectado con elemento presente, forzando limpieza y reinicio:', message);
          cleanupIncompleteBootstrap();
          
          // Reiniciar después de limpiar
          setTimeout(() => {
            const element = document.getElementById('KambiBC');
            if (element && isMountedRef.current && startBootstrapRef.current && !isBootstrapReady) {
              // console.log('🔄 Reiniciando bootstrap de Kambi después de error con elemento presente...');
              startBootstrapRef.current();
            }
          }, 1000);
          
          return true;
        }
      }
      return false;
    };

    // Interceptor de errores globales para capturar errores de Kambi
    const originalErrorHandler = window.onerror;
    const errorHandler = (message, source, lineno, colno, error) => {
      if (handleKambiError(error, message)) {
        return true; // Prevenir que el error se propague
      }
      
      // Llamar al handler original si existe
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };

    window.onerror = errorHandler;

    // Interceptor de promesas rechazadas (unhandled promise rejections)
    const originalRejectionHandler = window.onunhandledrejection;
    const rejectionHandler = (event) => {
      const error = event.reason;
      const errorMessage = error?.message || error?.toString() || '';
      
      if (handleKambiError(error, errorMessage)) {
        event.preventDefault(); // Prevenir que el error se propague
        return;
      }
      
      // Llamar al handler original si existe
      if (originalRejectionHandler) {
        return originalRejectionHandler(event);
      }
    };

    window.addEventListener('unhandledrejection', rejectionHandler);

    const startBootstrap = async () => {
      // Guardar referencia para poder reiniciar después de errores
      startBootstrapRef.current = startBootstrap;
      
      // Verificar que el componente sigue montado antes de empezar
      if (!isMountedRef.current) {
        // console.log('Componente desmontado, cancelando bootstrap');
        return;
      }
      
      // Limpiar cualquier bootstrap anterior incompleto antes de empezar uno nuevo
      if (bootstrapPromiseRef.current) {
        // Si hay un bootstrap en progreso, esperar a que termine o cancelarlo
        try {
          await Promise.race([
            bootstrapPromiseRef.current,
            sleep(100)
          ]);
        } catch (error) {
          // Ignorar errores del bootstrap anterior
        }
      }
      
      // Limpiar estado previo antes de iniciar uno nuevo (limpieza forzada)
      cleanupIncompleteBootstrap();
      
      // Resetear la promesa
      bootstrapPromiseRef.current = null;
      
      // Pequeño delay para asegurar que la limpieza se complete
      await sleep(100);
      
      // Verificar nuevamente que el componente sigue montado después de la limpieza
      if (!isMountedRef.current) {
        return;
      }

      bootstrapPromiseRef.current = (async () => {
        try {
          // Verificar que el componente sigue montado
          if (!isMountedRef.current) {
            return;
          }

          // Usar el ref para obtener el valor actual de isLogged
          const kambiConfig = await getKambiConfig(isLoggedRef.current);
        
        // Verificar nuevamente que el componente sigue montado
        if (!isMountedRef.current) {
          cleanupIncompleteBootstrap();
          return;
        }

        window._kc = kambiConfig;
        
        // Carga el script de Kambi BC, una vez cargado
        // se monta sus componentes dentro del contenedor #KambiBC
        await loadScript();
        
        // Verificar que el componente sigue montado y que el elemento existe
        if (!isMountedRef.current) {
          cleanupIncompleteBootstrap();
          return;
        }

        const kambiBCElement = document.getElementById('KambiBC');
        if (!kambiBCElement) {
          // console.warn('Elemento #KambiBC no encontrado después de cargar el script');
          window.__KAMBI_ALLOW_INIT = false;
          cleanupIncompleteBootstrap();
          return;
        }

        // Permitir que Kambi se inicialice solo si el elemento existe y el componente está montado
        window.__KAMBI_ALLOW_INIT = isMountedRef.current && !!kambiBCElement;

        // Observar el elemento #KambiBC para detectar si desaparece del DOM
        elementObserverRef.current = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                // Verificar si el nodo removido es #KambiBC o lo contiene
                if (node.id === 'KambiBC' || node.querySelector && node.querySelector('#KambiBC')) {
                  // console.warn('Elemento #KambiBC fue removido del DOM durante la carga');
                  cleanupIncompleteBootstrap();
                }
              }
            });
          });
        });

        // Observar cambios en el árbol del DOM alrededor del elemento
        elementObserverRef.current.observe(document.body, {
          childList: true,
          subtree: true,
        });

        // Verificar periódicamente que el elemento sigue existiendo y montado
        checkIntervalRef.current = setInterval(() => {
          if (!isMountedRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
            cleanupIncompleteBootstrap();
            return;
          }

          const element = document.getElementById('KambiBC');
          if (!element) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
            window.__KAMBI_ALLOW_INIT = false;
            // console.warn('Elemento #KambiBC desapareció durante la carga');
            cleanupIncompleteBootstrap();
            return;
          }
          
          // Actualizar el flag según el estado actual
          window.__KAMBI_ALLOW_INIT = isMountedRef.current && !!element;
        }, 100);

        // Limpiar el intervalo después de un tiempo razonable (30 segundos)
        setTimeout(() => {
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
          }
        }, 30000);

        setIsBootstrapReady(true);

        } catch (error) { 
          console.error('Error al iniciar el bootstrap de Kambi:', error);
          cleanupIncompleteBootstrap();
        } finally {
          bootstrapPromiseRef.current = null;
        }
      })();

      return bootstrapPromiseRef.current;
    };

    const checkAndStartBootstrap = () => {
      const kambiBCElement = document.getElementById('KambiBC');
      if (kambiBCElement && isMountedRef.current) {
        startBootstrap();
        return true;
      }

      return false;
    };

    // Verificar si el elemento ya existe
    if (checkAndStartBootstrap()) {
      return;
    }

    // Si no existe, observar cambios en el DOM hasta que aparezca
    initialObserverRef.current = new MutationObserver(() => {
      if (checkAndStartBootstrap()) {
        if (initialObserverRef.current) {
          initialObserverRef.current.disconnect();
          initialObserverRef.current = null;
        }
      }
    });

    initialObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      isMountedRef.current = false;
      window.__KAMBI_ALLOW_INIT = false;
      startBootstrapRef.current = null;
      
      if (initialObserverRef.current) {
        initialObserverRef.current.disconnect();
        initialObserverRef.current = null;
      }
      
      // Restaurar los handlers de errores originales
      if (originalErrorHandler) {
        window.onerror = originalErrorHandler;
      } else {
        window.onerror = null;
      }
      
      // Remover el listener de promesas rechazadas
      window.removeEventListener('unhandledrejection', rejectionHandler);
      
      // Limpiar observadores e intervalos
      if (elementObserverRef.current) {
        elementObserverRef.current.disconnect();
        elementObserverRef.current = null;
      }
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      
      // Limpiar el verificador de estado inconsistente
      if (inconsistentStateCheckerRef.current) {
        clearInterval(inconsistentStateCheckerRef.current);
        inconsistentStateCheckerRef.current = null;
      }
      
      // Si el bootstrap está en progreso, limpiar inmediatamente
      if (bootstrapPromiseRef.current) {
        cleanupIncompleteBootstrap();
        setIsBootstrapReady(false);
      }
    };
  }, []);


  return { isBootstrapReady, isScriptError };
};
